import React, { useTransition } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Circle, useMapEvents, useMap } from 'react-leaflet';
import { Button } from 'react-bootstrap';
import L from 'leaflet';
import { useTranslation } from 'react-i18next';
import { loQueSeaAFloat } from '../utils/TypeUtils';

// Custom icons for online and offline players
const playerOnlineIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const playerOfflineIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Custom icons for online and offline searchers
const searcherOnlineIcon = new L.Icon({
  iconUrl: '/pelota.webp',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const searcherOfflineIcon = new L.Icon({
  iconUrl: '/pelota.webp',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const footballIcon = new L.Icon({
  iconUrl: '/football-logo.png',
  iconSize: [32, 32],
  iconAnchor: [16, 32],
  popupAnchor: [0, -32],
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
  shadowSize: [32, 32]
});

// Explicitly define the default Leaflet icon (for other markers if any)
const defaultIcon = L.icon({
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

// Set the default icon for all markers (can be overridden by individual Marker components)
L.Marker.prototype.options.icon = defaultIcon;

export interface MapMarker {
  id: string;
  latitude: number;
  longitude: number;
  name: string;
  isOnline?: boolean; // Optional, as not all markers might have this
  type: 'player' | 'searcher' | 'location'; // Distinguish between player, searcher, or a generic location
  gameAddress?: string; // Specific to searchers
  gameDate?: string; // Specific to searchers
  gameTime?: string; // Specific to searchers
  contact?: string; // Specific to players
  // New properties for player actions
  hasActiveChat?: boolean;
  onSendMessageClick?: (player: any) => void;
  onViewChatClick?: (playerId: string) => void;
}

interface MapComponentProps {
  center: [number, number];
  zoom: number;
  onMapClick?: (latlng: { lat: number; lng: number }) => void;
  children?: React.ReactNode;
  markers?: MapMarker[]; // Generic prop for all markers
  // New props for player actions
  searcherUniqueId?: string | null;
  searcherName?: string;
  gameAddress?: string;
  searchRadiusCircle?: { center: { latitude: number; longitude: number }; radius: number }; // New prop for search radius circle
}

const MapClickHandler: React.FC<{ onMapClick: (latlng: { lat: number; lng: number }) => void }> = ({ onMapClick }) => {
  useMapEvents({
    click: (e) => {
      onMapClick(e.latlng);
    },
  });
  return null;
};

// Component to handle map view changes
const ChangeView: React.FC<{ center: [number, number]; zoom: number }> = ({ center, zoom }) => {
  const map = useMap();
  React.useEffect(() => {
    map.setView(center, zoom);
  }, [center, zoom, map]);
  return null;
};

const MapComponent: React.FC<MapComponentProps> = ({ center, zoom, onMapClick, children, markers, searcherUniqueId, searcherName, gameAddress, searchRadiusCircle }) => {
  const getContactLink = (contact: string) => {
    if (contact.includes('@')) {
      return <a href={`mailto:${contact}`}>{contact}</a>;
    } else if (/^\+?[0-9\s-()]{7,20}$/.test(contact)) { // Basic phone number regex
      return <a href={`tel:${contact.replace(/\s/g, '')}`}>{contact}</a>;
    } else {
      return contact;
    }
  };

  const { t } = useTranslation();

  return (
    <MapContainer center={center} zoom={zoom} style={{ height: '400px', width: '100%' }}>
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      {onMapClick && <MapClickHandler onMapClick={onMapClick} />}
      <ChangeView center={center} zoom={zoom} />
      {children}

      {searchRadiusCircle && (
        <Circle center={[searchRadiusCircle.center.latitude, searchRadiusCircle.center.longitude]} radius={searchRadiusCircle.radius * 1000} pathOptions={{ color: 'blue', fillOpacity: 0.1 }} />
      )}

      {markers && markers.map(marker => {
        let icon = defaultIcon;
        let popupContent = (
          <Popup>
            <b>{marker.name}</b><br />
            Lat: {loQueSeaAFloat(marker.latitude).toFixed(4)}, Lon: {loQueSeaAFloat(marker.longitude).toFixed(4)}
          </Popup>
        );

        if (marker.type === 'player') {
          icon = marker.isOnline ? playerOnlineIcon : playerOfflineIcon;
          popupContent = (
            <Popup>
              <b>{marker.name}</b><br />
              Contact: {marker.contact ? getContactLink(marker.contact) : 'N/A'}<br />
              Status: {marker.isOnline ? 'Online' : 'Offline'}<br />
              {marker.isOnline && (
                marker.hasActiveChat ? (
                  <Button variant="info" size="sm" className="mt-2" onClick={() => marker.onViewChatClick && marker.onViewChatClick(marker.id)}>
                    {t('viewChat')}
                  </Button>
                ) : (
                  <Button variant="success" size="sm" className="mt-2" onClick={() => marker.onSendMessageClick && marker.onSendMessageClick(marker)} disabled={!searcherUniqueId || !searcherName || !gameAddress}>
                    {t('sendMessage')}
                  </Button>
                )
              )}
            </Popup>
          );
        } else if (marker.type === 'searcher') {
          
          // icon = marker.isOnline ? searcherOnlineIcon : searcherOfflineIcon; // Use appropriate icon for searchers
          icon = footballIcon; // Use the football icon for searchers
          
          popupContent = (
            <Popup>
              <b>{marker.name}</b><br />
              {t('gameDate')}: {marker.gameDate || 'N/A'}<br />
              {t('gameTime')}: {marker.gameTime || 'N/A'}<br />
              {t('gameAddress')}: {marker.gameAddress || 'N/A'}<br />
              Status: {marker.isOnline ? 'Online' : 'Offline'}<br />
              {marker.isOnline && (
                marker.hasActiveChat ? (
                  <Button variant="info" size="sm" className="mt-2" onClick={() => marker.onViewChatClick && marker.onViewChatClick(marker.id)}>
                    {t('viewChat')}
                  </Button>
                ) : (
                  <Button variant="success" size="sm" className="mt-2" onClick={() => marker.onSendMessageClick && marker.onSendMessageClick(marker)}>
                    {t('sendMessage')}
                  </Button>
                )
              )}
            </Popup>
          );
        } else if (marker.type === 'location') {
          // For generic location markers, default icon is fine
          popupContent = (
            <Popup>
              <b>{marker.name}</b><br />
              Lat: {loQueSeaAFloat(marker.latitude).toFixed(4)}, Lon: {loQueSeaAFloat(marker.longitude).toFixed(4)}
            </Popup>
          );
        }

        return (
          <Marker
            key={marker.id}
            position={[marker.latitude, marker.longitude]}
            icon={icon}
          >
            {popupContent}
          </Marker>
        );
      })}
    </MapContainer>
  );
};

export default MapComponent;
